import Image from 'next/image';
import { useTranslation } from 'next-export-i18n';
import React from 'react';

import VerifyIcon from '@/assets/verified-white.svg';
import SimpleCard from '@/components/cards/Card';
import VBImage from '@/components/images/VBImage';
import DefaultTag from '@/components/tags/DefaultTag';
import type CompanyProps from '@/types/Company.type';
import { TagType } from '@/utils/enum';
import {
  getAllLocations,
  getAllRegions,
  getCommonPropsDisplay,
  getCompanyCover,
} from '@/utils/helper';

type OverviewItemProps = {
  title: string;
  value: string | boolean;
};

type OverviewProps = {
  company: CompanyProps;
};

const OverviewItem = ({ title, value }: OverviewItemProps) => {
  const isActive = value === 'active';
  const { t } = useTranslation();

  return (
    <div className="mb-7 flex flex-col md:flex-row">
      <h4 className="mb-1 w-full flex-auto text-base md:mb-0 md:w-24">
        {title}
      </h4>
      <h4 className="w-full flex-auto text-base font-medium capitalize md:w-64">
        {isActive && (
          <DefaultTag
            title={value ? t('company.active') : t('company.inActive')}
            tagType={value ? TagType.Success : TagType.Error}
            className="px-4 text-sm font-bold uppercase"
          />
        )}
        {!isActive && value}
      </h4>
    </div>
  );
};

const Overview = ({
  company: {
    name,
    logo,
    short_description,
    description,
    founding_year,
    business_models,
    industries,
    segments,
    operation_status,
    specific_locations,
    is_verified,
  },
}: OverviewProps) => {
  const { t } = useTranslation();
  const cover = getCompanyCover(logo);

  return (
    <SimpleCard padding="p-0">
      <div className="h-28 bg-sky-900 sm:h-32">
        <div className="p-5">
          <div className="mt-4 flex flex-row items-center sm:items-center">
            <div className="logo-container relative h-24 w-24 flex-none overflow-hidden rounded-xl sm:h-28 sm:w-28">
              {cover && <VBImage src={cover} alt={'image'} />}
            </div>
            <h1 className="flex grow flex-row px-4 text-2xl font-medium capitalize text-white sm:text-3xl">
              <span>{name}</span>
              {is_verified && (
                <span className="ml-2 flex-none md:hidden">
                  <Image
                    className="flex-none"
                    src={VerifyIcon}
                    alt="verified"
                  />
                </span>
              )}
            </h1>

            {is_verified && (
              <div className="my-3 hidden flex-row md:flex">
                <Image className="flex-none" src={VerifyIcon} alt="verified" />
                <span className="ml-2 font-medium text-white">
                  {t('company.verified')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 px-5 pt-5 md:pb-5">
        <h2 className="font-bold">{t('company.overview')}</h2>
        <p className="mt-2 text-base">{description || short_description}</p>

        <div className="mt-10">
          <OverviewItem title="Founded" value={founding_year} />
          <OverviewItem
            title={t('company.businessMode')}
            value={getCommonPropsDisplay(business_models)}
          />
          <OverviewItem
            title={t('company.industry')}
            value={getCommonPropsDisplay(industries)}
          />
          <OverviewItem
            title={t('company.segment')}
            value={getCommonPropsDisplay(segments)}
          />
          <OverviewItem
            title={t('company.region')}
            value={getAllRegions(specific_locations)}
          />
          <OverviewItem
            title={t('company.operatingStatus')}
            value={operation_status}
          />
          <OverviewItem
            title={t('company.hqLocation')}
            value={getAllLocations(specific_locations)}
          />
        </div>
      </div>
    </SimpleCard>
  );
};

export default Overview;
